import React, { useState } from 'react'
import { Input, TextField, IconButton, InputAdornment } from '@mui/material'
import type { SxProps, Theme } from '@mui/material/styles'
import type { TextFieldProps } from '@mui/material/TextField'
import LockIcon from '@mui/icons-material/Lock'
import Visibility from '@mui/icons-material/Visibility'
import VisibilityOff from '@mui/icons-material/VisibilityOff'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'

/**
 * The one password field *with a visibility toggle*, in two skins.
 *
 * The qualifier is load-bearing, because this docblock will be read as the map of where password
 * input lives and it is not the whole map. Four masked fields still bypass this component and
 * have no toggle at all: `VirtualRoom/PasswordEntryModal.tsx`,
 * `CustomRoomTable.tsx` and `MainMenu.tsx` (the admin API-key fields under `EnvironmentConfig/`
 * are a separate case — secrets, not passwords). The sharpest of those is `PasswordEntryModal`,
 * the modal you type a room password into to *join*, which sits in the same folder as the
 * `PasswordManageDialog` this component now serves — so you can reveal the password you set on a
 * room but not the one you type to enter it. That is the same inconsistency that prompted SC-400,
 * one screen over, and it is deliberately left for its own ticket rather than widened into this
 * refactor.
 *
 * It used to be the one password field for Sign In and Create Account only, and every other
 * screen hand-rolled its own `TextField` + eye `IconButton`: the reset-password page, the
 * profile password panel, and the room password dialog — nine fields across three files, each
 * free to drift. They did. The reset page hardcoded a grey icon that hovers orange; nothing
 * else hovers orange. Only this component disabled the toggle along with the field, so
 * everywhere else the eye stayed live mid-submit. And a fix applied to one never reached the
 * others, which is how a confirm field ended up borrowing the *new* password's toggle state
 * on one screen (see `confirmPasswordVisibilityToggle.test.ts`).
 *
 * The two skins exist because the screens genuinely differ, not because the behaviour should:
 *
 * - **`auth`** — the borderless `Input` with `sevv-auth-*` classes, sized and
 *   coloured by the auth stylesheet. Sign In and Create Account. Unchanged.
 * - **`outlined`** — a standard MUI outlined `TextField` with a floating label, which is what
 *   the other three screens already render. It forwards the full `TextField` API, so each
 *   caller keeps its own `sx`, `margin`, `helperText` and error wiring and looks exactly as it
 *   did; what it stops owning is the toggle.
 *
 * Whichever skin, the toggle is now decided in one place: which icon means what, that the
 * button never submits its form, that it disables with the field, and that its accessible name
 * is `Show …` / `Hide …` built from `visibilityLabelNoun`.
 */

// showPassword and onToggleShowPassword must be supplied together or not at
// all — the union rules out a caller passing one without the other, which
// would otherwise leave the visibility toggle enabled but non-functional.
type PasswordVisibilityProps =
  | { showPassword?: undefined; onToggleShowPassword?: undefined }
  | { showPassword: boolean; onToggleShowPassword: () => void }

/** Shared by both skins: what the eye is called, and how big it is drawn. */
interface ToggleProps {
  /**
   * Completes "Show …" / "Hide …" for the toggle's accessible name. Defaults to `password`;
   * pass `password confirmation`, `current password` or `new password` where a screen has more
   * than one field, so a screen reader can tell them apart.
   */
  visibilityLabelNoun?: string
  /** `small` matches a dense dialog; the default matches a full-size form field. */
  iconSize?: 'small' | 'medium'
}

interface PasswordInputBaseProps {
  id: string
  value: string
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void
  onFocus?: () => void
  onBlur?: () => void
  placeholder?: string
  autoComplete?: string
  disabled?: boolean
  required?: boolean
  hasError?: boolean
  errorId?: string
  errorMessage?: React.ReactNode
}

// `variant` is REQUIRED on both skins, and that is the whole mechanism — it is not ceremony.
// TypeScript's excess-property check against a *union* admits any key present in either member,
// so while `auth` was the silent default, `<PasswordInput label="New password" sx={{…}} />`
// compiled clean and then rendered a borderless, unlabelled field: the auth branch destructures
// only the base props, so `label`, `sx`, `margin` and `helperText` landed nowhere. Naming the
// variant makes the union discriminated, so TypeScript narrows to one member and rejects
// anything that member does not declare — including props like `className` that would otherwise
// compile and silently do nothing.
//
// The first attempt at this declared every outlined-only key as `?: never` on the auth skin
// instead, which kept the default but was far too blunt: `keyof TextFieldProps` swallowed ~286
// keys, so `className`, `autoFocus`, `name`, `onKeyDown` and `inputRef` were all rejected, and
// `key` among them — meaning an auth field could not be rendered inside a `.map()` at all, with
// an error message that was a wall of union text naming none of it. Requiring the discriminant
// costs two call sites and gets a two-line error that names the offending prop.
type AuthVariantProps = { variant: 'auth' } & PasswordInputBaseProps & ToggleProps

type OutlinedVariantProps = {
  variant: 'outlined'
  /** Draws the lock in the leading adornment. The reset page has one; the other two do not. */
  showLockIcon?: boolean
  /** Styling for the toggle button itself, kept separate from the field's own `sx`. */
  toggleSx?: SxProps<Theme>
} & ToggleProps &
  // `type`, the variant and both adornments are this component's job. `InputProps` is omitted
  // rather than merged because merging it half-worked in a way no caller could predict: the end
  // adornment was always overwritten by the toggle, while a caller's `startAdornment` survived
  // or vanished depending on `showLockIcon`. Two adornments under two different rules, keyed off
  // an unrelated boolean. `inputProps` (lowercase — the attributes for the `<input>` itself) is
  // deliberately still forwarded; it is a different prop and nothing here contends for it.
  Omit<TextFieldProps, 'type' | 'variant' | 'InputProps'>

export type PasswordInputProps = (AuthVariantProps | OutlinedVariantProps) &
  PasswordVisibilityProps

/** Resolve controlled vs internal visibility once, for either skin. */
function useVisibility(
  controlled: boolean | undefined,
  onToggle: (() => void) | undefined
): [boolean, () => void] {
  const [internal, setInternal] = useState(false)
  const isControlled = controlled !== undefined
  const shown = isControlled ? controlled : internal
  const toggle = isControlled
    ? (onToggle as () => void)
    : () => setInternal((prev) => !prev)
  return [shown, toggle]
}

/**
 * The eye. `type="button"` is belt and braces — MUI's ButtonBase already defaults it — but it
 * states the intent at the call site, which is where a reader checking "does this submit the
 * form?" will look.
 */
function VisibilityToggle({
  shown,
  onToggle,
  noun = 'password',
  iconSize,
  disabled,
  className,
  sx,
}: {
  shown: boolean
  onToggle: () => void
  noun?: string
  iconSize?: 'small' | 'medium'
  disabled?: boolean
  className?: string
  sx?: SxProps<Theme>
}) {
  return (
    <InputAdornment position="end">
      <IconButton
        className={className}
        onClick={onToggle}
        type="button"
        aria-label={`${shown ? 'Hide' : 'Show'} ${noun}`}
        disabled={disabled}
        edge="end"
        size={iconSize}
        sx={sx}
      >
        {shown ? <Visibility fontSize={iconSize} /> : <VisibilityOff fontSize={iconSize} />}
      </IconButton>
    </InputAdornment>
  )
}

export function PasswordInput(props: PasswordInputProps) {
  const [showPassword, toggleShowPassword] = useVisibility(
    props.showPassword,
    props.onToggleShowPassword
  )

  if (props.variant === 'outlined') {
    const {
      variant: _variant,
      showLockIcon,
      toggleSx,
      visibilityLabelNoun,
      iconSize,
      showPassword: _shown,
      onToggleShowPassword: _onToggle,
      ...textFieldProps
    } = props

    return (
      <TextField
        {...textFieldProps}
        type={showPassword ? 'text' : 'password'}
        InputProps={{
          ...(showLockIcon
            ? {
                startAdornment: (
                  <InputAdornment position="start">
                    <LockIcon sx={{ color: 'var(--sevv-gray-400, #bdbdbd)' }} />
                  </InputAdornment>
                ),
              }
            : {}),
          endAdornment: (
            <VisibilityToggle
              shown={showPassword}
              onToggle={toggleShowPassword}
              noun={visibilityLabelNoun}
              iconSize={iconSize}
              disabled={textFieldProps.disabled}
              sx={toggleSx}
            />
          ),
        }}
      />
    )
  }

  const {
    id,
    value,
    onChange,
    onFocus,
    onBlur,
    placeholder = 'Password',
    autoComplete = 'current-password',
    disabled = false,
    required = true,
    hasError = false,
    errorId,
    errorMessage,
    visibilityLabelNoun,
    iconSize,
  } = props

  const resolvedErrorId = errorId ?? `${id}-error`
  const showError = hasError && !!errorMessage

  return (
    <div className="sevv-auth-input-group">
      <Input
        id={id}
        className={`sevv-auth-input-mui ${hasError ? 'sevv-auth-input-error' : ''}`}
        disableUnderline
        fullWidth
        type={showPassword ? 'text' : 'password'}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        onFocus={onFocus}
        onBlur={onBlur}
        autoComplete={autoComplete}
        disabled={disabled}
        required={required}
        inputProps={{
          'aria-invalid': hasError,
          'aria-describedby': showError ? resolvedErrorId : undefined,
        }}
        startAdornment={
          <InputAdornment position="start">
            <LockIcon className="sevv-auth-input-icon-mui" />
          </InputAdornment>
        }
        endAdornment={
          <VisibilityToggle
            className="sevv-auth-input-toggle-mui"
            shown={showPassword}
            onToggle={toggleShowPassword}
            noun={visibilityLabelNoun}
            iconSize={iconSize}
            disabled={disabled}
          />
        }
      />
      {showError && (
        <div id={resolvedErrorId} className="sevv-auth-input-error-text">
          <ErrorOutlineIcon sx={{ fontSize: 16, verticalAlign: 'middle', mr: 0.5 }} />
          {errorMessage}
        </div>
      )}
    </div>
  )
}

export default PasswordInput
